import React from 'react';
import { CheckIcon } from '../../icons';
import { CrmUser } from '../../../types';
import { Button } from '../../ui/Button';
import SearchInput from '../../ui/SearchInput';

interface AgentsFilterPanelProps {
    panelRef: React.Ref<HTMLDivElement>;
    position: { top: number; left: number };
    searchId: string;
    query: string;
    onQueryChange: (value: string) => void;
    users: CrmUser[];
    draftIds: string[];
    currentUserId?: string;
    onToggleUser: (id: string) => void;
    onClear: () => void;
    onApply: () => void;
}

const AgentsFilterPanel: React.FC<AgentsFilterPanelProps> = ({
    panelRef,
    position,
    searchId,
    query,
    onQueryChange,
    users,
    draftIds,
    currentUserId,
    onToggleUser,
    onClear,
    onApply,
}) => (
    <section
        ref={panelRef}
        role="dialog"
        aria-label="Filtrar por responsável"
        aria-modal="true"
        style={{ top: position.top, left: position.left }}
        className="fixed z-[9999] flex w-[min(20rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-lg"
    >
        <header className="border-b border-border p-s3">
            <SearchInput
                id={searchId}
                name={searchId}
                value={query}
                onChange={onQueryChange}
                placeholder="Buscar usuário..."
                autoFocus
            />
        </header>

        <ul
            role="listbox"
            aria-multiselectable="true"
            aria-label="Usuários"
            className="max-h-[min(17.5rem,50vh)] flex-1 space-y-s1 overflow-y-auto p-s2"
        >
            {users.map((user) => {
                const selected = draftIds.includes(user.id);
                return (
                    <li key={user.id}>
                        <button
                            type="button"
                            role="option"
                            aria-selected={selected}
                            onClick={() => onToggleUser(user.id)}
                            className={`flex min-h-11 w-full items-center gap-s3 rounded-lg p-s2 text-left transition-colors md:min-h-0 ${
                                selected ? 'bg-primary/10' : 'hover:bg-muted/50'
                            }`}
                        >
                            <span className="relative shrink-0">
                                <span
                                    className={`flex size-8 items-center justify-center overflow-hidden rounded-full text-xs font-semibold ${
                                        selected ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                                    }`}
                                    aria-hidden
                                >
                                    {user.avatar
                                        ? <img src={user.avatar} alt="" className="size-full object-cover" />
                                        : user.name.substring(0, 2).toUpperCase()}
                                </span>
                                {selected && (
                                    <span className="absolute -right-0.5 -top-0.5 flex size-4 items-center justify-center rounded-full border-2 border-card bg-primary">
                                        <CheckIcon className="size-2.5 text-primary-foreground" />
                                    </span>
                                )}
                            </span>
                            <span className="min-w-0 flex-1">
                                <span className="flex items-center gap-s2">
                                    <span className={`truncate text-sm font-medium ${selected ? 'text-primary' : 'text-foreground'}`}>
                                        {user.name}
                                    </span>
                                    {user.id === currentUserId && (
                                        <span className="badge badge-neutral shrink-0">eu</span>
                                    )}
                                </span>
                                <span className="mt-0.5 block truncate text-sm text-muted-foreground">{user.accessType}</span>
                            </span>
                        </button>
                    </li>
                );
            })}
            {users.length === 0 && (
                <li>
                    <p role="status" className="py-s8 text-center text-sm text-muted-foreground">
                        Nenhum usuário encontrado
                    </p>
                </li>
            )}
        </ul>

        <footer className="flex items-center justify-between gap-s3 border-t border-border bg-card p-s3">
            <Button type="button" variant="ghost" size="sm" onClick={onClear} className="min-h-11 shadow-none md:min-h-8">
                Limpar
            </Button>
            <Button type="button" size="sm" onClick={onApply} className="min-h-11 px-s4 md:min-h-8">
                Aplicar
            </Button>
        </footer>
    </section>
);

export default AgentsFilterPanel;
